import { useState } from 'react';
import { Download, FileText, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api, { DocumentSignFileResponse } from '@/lib/api';
import { useDeleteDocumentFile } from '../hooks';

interface DocumentListProps {
  envelopeId: string;
  files: DocumentSignFileResponse[];
  /** Disable mutating actions (used when envelope is no longer a draft). */
  readOnly?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentList({ envelopeId, files, readOnly = false }: DocumentListProps) {
  const deleteFile = useDeleteDocumentFile(envelopeId);
  const [downloading, setDownloading] = useState<string | null>(null);

  if (files.length === 0) {
    return <p className="text-sm text-muted-foreground">No documents uploaded yet.</p>;
  }

  async function handleDownload(file: DocumentSignFileResponse) {
    setDownloading(file.fileId);
    try {
      const { downloadUrl } = await api.getDocumentSignFileDownloadUrl(envelopeId, file.fileId);
      // Open in a new tab so the user can keep editing the envelope.
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(null);
    }
  }

  return (
    <ul className="space-y-2">
      {files.map((file) => (
        <li key={file.fileId} className="flex items-center gap-3 rounded-md border bg-card p-3">
          <FileText className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{file.fileName}</div>
            <div className="text-xs text-muted-foreground">
              {formatBytes(file.sizeBytes)}
              {file.pageCount !== null && file.pageCount !== undefined
                ? ` · ${file.pageCount} ${file.pageCount === 1 ? 'page' : 'pages'}`
                : ''}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={downloading === file.fileId}
            onClick={() => handleDownload(file)}
            aria-label={`Download ${file.fileName}`}
          >
            <Download className="h-4 w-4" aria-hidden="true" />
          </Button>
          {!readOnly && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={deleteFile.isPending}
              onClick={() => deleteFile.mutate(file.fileId)}
              aria-label={`Delete ${file.fileName}`}
            >
              <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}
